import React, {
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  ConsentCategories,
  loadConsentState,
  saveConsent,
} from "./consent";

type ConsentContextValue = {
  consent: ConsentCategories;
  setConsent: (c: ConsentCategories) => void;
  isDecided: boolean;
  preferencesOpen: boolean;
  openPreferences: () => void;
  closePreferences: () => void;
};

const ConsentContext = createContext<ConsentContextValue | null>(null);
export const useConsent = () => {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used within ConsentProvider");
  return ctx;
};

export const ConsentProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [{ consent, isDecided }, setConsentState] = useState(loadConsentState);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const setConsent = (c: ConsentCategories) => {
    saveConsent(c);
    setConsentState({ consent: c, isDecided: true });

    window.dispatchEvent(new CustomEvent("consent:updated", { detail: c }));
  };

  const value = useMemo(
    () => ({
      consent,
      setConsent,
      isDecided,
      preferencesOpen,
      openPreferences: () => setPreferencesOpen(true),
      closePreferences: () => setPreferencesOpen(false),
    }),
    [consent, isDecided, preferencesOpen]
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
};
